import { ResolveFn } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { inject } from '@angular/core';
import { ProjectSessionService } from './project-session.service';

/**
 * Restores a project into the wizard before its page renders.
 *
 * A failed load does not cancel the navigation. Letting the error through
 * would leave the requester on the page they started from with nothing
 * happening and no explanation; instead the route opens and
 * `hydrationFailed` lets the page say the saved work could not be loaded,
 * which is the one thing they must not be left guessing about.
 */
export const projectSessionResolver: ResolveFn<boolean> = (route) => {
  const projectId = route.paramMap.get('projectId') ?? route.queryParamMap.get('projectId');
  const session = inject(ProjectSessionService);

  if (!projectId) {
    return of(true);
  }

  return session.hydrateProject(projectId).pipe(
    map(() => true),
    catchError(() => {
      session.markHydrationFailed(projectId);
      return of(true);
    })
  );
};

/**
 * Clears any project left over from an earlier session before a fresh
 * intake starts.
 *
 * `/requirements-intake` (unprefixed) is the one route the app treats as
 * "no project yet" — everything else is reached under `/projects/:projectId`
 * once one exists. `ProjectSessionService` is a root singleton, so without
 * this, reaching this route with a project already loaded (e.g. "Start New
 * Project" from Home) would carry its id forward and the next analysis would
 * silently attach to that old project instead of creating a new one.
 */
export const freshProjectResolver: ResolveFn<boolean> = () => {
  inject(ProjectSessionService).reset();
  return true;
};
